// Instagram Stüdyosu — marka bağımsız iç araç (beta, 30 Ağu 2026).
//
// Konu → Gemini görsel → caption → taslak → YAYINLA. Hesap Glowytics'in kendi
// Instagram'ı, bir müşteri markası değil; bu yüzden sayfa marka seçiminden
// tamamen bağımsız çalışır (ProtectedRoute requireBrand={false}).
//
// TASARIM KARARI — yayın TEK YÖNLÜ KAPI. Instagram API'si yayınlanan gönderiyi
// silmiyor, yalnızca uygulamadan elle siliniyor. Bu yüzden "üret" ile "yayınla"
// asla aynı düğme değil: önce taslak olur, taslak gözden geçirilir, yayın ayrıca
// onaylanır. Otomasyonun tamamı buraya bağlanacak ama bu kapı korunacak.
//
// Buradaki e-posta kilidi KOZMETİK. Asıl kilit backend'de
// (routes/instagram.js → IZINLI_EPOSTALAR).

import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Instagram, RefreshCw, Sparkles, Link2, Trash2, Send, AlertTriangle,
  CheckCircle2, Clock, Image as ImageIcon, KeyRound, ExternalLink, Loader2, Layers,
  X, ChevronLeft, ChevronRight,
} from 'lucide-react';
import { apiFetch } from '../../utils/apiFetch';

const API = 'https://api.glowytics.com/api/instagram';

const CAPTION_SINIRI = 2200; // Instagram'ın açıklama sınırı

/* ─────────────────────────── yardımcılar ─────────────────────────── */

async function cagir(yol, secenekler = {}) {
  const res = await apiFetch(`${API}${yol}`, {
    headers: { 'Content-Type': 'application/json' },
    ...secenekler,
  });
  const govde = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(govde.error || `HTTP ${res.status}`);
  return govde;
}

const tarih = (d) => (d ? new Date(d).toLocaleString('tr-TR', { dateStyle: 'medium', timeStyle: 'short' }) : '—');

/** Bugünden verilen tarihe kalan tam gün. Geçmişse negatif. */
function kalanGun(d) {
  if (!d) return null;
  return Math.floor((new Date(d) - new Date()) / 86400000);
}

/* ─────────────────────────── küçük parçalar ─────────────────────────── */

function Rozet({ tur = 'nötr', children }) {
  const renk = {
    iyi: 'bg-emerald-50 text-emerald-700 ring-emerald-200',
    uyari: 'bg-amber-50 text-amber-700 ring-amber-200',
    kotu: 'bg-rose-50 text-rose-700 ring-rose-200',
    nötr: 'bg-slate-100 text-slate-600 ring-slate-200',
    mor: 'bg-violet-50 text-violet-700 ring-violet-200',
  }[tur];
  return (
    <span className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[11px] font-medium ring-1 ${renk}`}>
      {children}
    </span>
  );
}

function Alan({ etiket, children }) {
  return (
    <div className="min-w-0">
      <div className="text-[11px] uppercase tracking-wide text-slate-400">{etiket}</div>
      <div className="truncate text-sm text-slate-700">{children}</div>
    </div>
  );
}

function Uyari({ tur = 'kotu', children }) {
  if (!children) return null;
  const renk = tur === 'uyari'
    ? 'border-amber-200 bg-amber-50 text-amber-800'
    : 'border-rose-200 bg-rose-50 text-rose-800';
  return (
    <div className={`flex items-start gap-2 rounded-lg border px-3 py-2 text-sm ${renk}`}>
      <AlertTriangle className="mt-0.5 h-4 w-4 flex-shrink-0" />
      <div className="min-w-0 break-words">{children}</div>
    </div>
  );
}

/* ─────────────────────────── bağlantı kartı ─────────────────────────── */

function BaglantiKarti({ durum, yukleniyor, onYenile, onKaydet, onSil }) {
  const [acik, setAcik] = useState(false);
  const [token, setToken] = useState('');
  const [kaydediliyor, setKaydediliyor] = useState(false);
  const [hata, setHata] = useState(null);

  const gun = kalanGun(durum?.dataAccessExpiresAt);
  const yayinIzni = (durum?.scopes || []).includes('instagram_content_publish');

  const kaydet = async () => {
    setKaydediliyor(true); setHata(null);
    try {
      await onKaydet(token.trim());
      setToken(''); setAcik(false);
    } catch (e) { setHata(e.message); } finally { setKaydediliyor(false); }
  };

  return (
    <section className="rounded-xl border border-slate-200 bg-white shadow-sm">
      <div className="flex items-center justify-between gap-3 border-b border-slate-100 px-5 py-3">
        <h2 className="flex items-center gap-2 text-sm font-semibold text-slate-800">
          <Link2 className="h-4 w-4 text-slate-400" /> Bağlantı
        </h2>
        <div className="flex items-center gap-2">
          <button
            onClick={onYenile}
            className="inline-flex items-center gap-1.5 rounded-lg border border-slate-200 px-2.5 py-1.5 text-xs font-medium text-slate-600 hover:bg-slate-50"
          >
            <RefreshCw className={`h-3.5 w-3.5 ${yukleniyor ? 'animate-spin' : ''}`} /> Yenile
          </button>
          <button
            onClick={() => setAcik((v) => !v)}
            className="inline-flex items-center gap-1.5 rounded-lg border border-slate-200 px-2.5 py-1.5 text-xs font-medium text-slate-600 hover:bg-slate-50"
          >
            <KeyRound className="h-3.5 w-3.5" /> Token'ı değiştir
          </button>
        </div>
      </div>

      <div className="px-5 py-4">
        {!durum?.bagli ? (
          <div className="text-sm text-slate-500">
            Kayıtlı hesap yok. Yukarıdan bir erişim token'ı girin.
          </div>
        ) : (
          <>
            <div className="flex flex-wrap items-center gap-4">
              {durum.profilePictureUrl && (
                <img
                  src={durum.profilePictureUrl}
                  alt=""
                  className="h-14 w-14 flex-shrink-0 rounded-full object-cover ring-2 ring-violet-100"
                />
              )}
              <div className="min-w-0">
                <div className="flex flex-wrap items-center gap-2">
                  <span className="text-base font-semibold text-slate-900">@{durum.username}</span>
                  {durum.gecerli
                    ? <Rozet tur="iyi"><CheckCircle2 className="h-3 w-3" /> token geçerli</Rozet>
                    : <Rozet tur="kotu"><AlertTriangle className="h-3 w-3" /> token geçersiz</Rozet>}
                  {durum.tokenType === 'PAGE'
                    ? <Rozet tur="mor">Sayfa token'ı · süresiz</Rozet>
                    : <Rozet tur="uyari">{durum.tokenType || 'bilinmiyor'} token'ı</Rozet>}
                  {!yayinIzni && <Rozet tur="kotu">yayın izni yok</Rozet>}
                </div>
                <div className="mt-1 text-xs text-slate-500">
                  {durum.followersCount != null && <>{durum.followersCount} takipçi · </>}
                  {durum.mediaCount != null && <>{durum.mediaCount} gönderi · </>}
                  son doğrulama {tarih(durum.guncellendi)}
                </div>
              </div>

              {durum.kota && (
                <div className="ml-auto w-44">
                  <div className="mb-1 flex justify-between text-[11px] text-slate-500">
                    <span>24 saatlik kota</span>
                    <span className="tabular-nums">{durum.kota.kullanilan}/{durum.kota.toplam}</span>
                  </div>
                  <div className="h-1.5 overflow-hidden rounded-full bg-slate-100">
                    <div
                      className="h-full rounded-full bg-violet-500"
                      style={{ width: `${Math.min(100, (durum.kota.kullanilan / (durum.kota.toplam || 1)) * 100)}%` }}
                    />
                  </div>
                </div>
              )}
            </div>

            <div className="mt-4 grid grid-cols-2 gap-x-6 gap-y-3 border-t border-slate-100 pt-4 sm:grid-cols-4">
              <Alan etiket="IG hesap ID">{durum.igUserId}</Alan>
              <Alan etiket="Sayfa ID">{durum.pageId || '—'}</Alan>
              <Alan etiket="App ID">{durum.appId || '—'}</Alan>
              <Alan etiket="Token">
                <code className="text-[11px] text-slate-500">{durum.tokenOnizleme}</code>
              </Alan>
            </div>

            {durum.hata && <div className="mt-3"><Uyari>{durum.hata}</Uyari></div>}

            {/* Token süresiz ama Meta'nın 90 günlük veri erişimi saati ayrı işler.
                Sunucu tarafında sessizce ölebilecek TEK şey bu, o yüzden görünür. */}
            {gun != null && (
              <div className="mt-3">
                <Uyari tur={gun < 14 ? 'kotu' : 'uyari'}>
                  <b>Veri erişimi {tarih(durum.dataAccessExpiresAt)}</b> tarihinde doluyor
                  {gun >= 0 ? ` (${gun} gün kaldı)` : ' (SÜRESİ GEÇTİ)'}.
                  Token'ın kendisi süresiz; biten şey Meta'nın 90 günlük veri erişimi saati.
                  O tarihten önce Facebook Login'den bir kez geçip token'ı yenilemek gerekiyor,
                  yoksa yayın sessizce durur.
                </Uyari>
              </div>
            )}

            <button
              onClick={onSil}
              className="mt-4 inline-flex items-center gap-1.5 text-xs font-medium text-rose-600 hover:text-rose-700"
            >
              <Trash2 className="h-3.5 w-3.5" /> Bağlantıyı sil
            </button>
          </>
        )}

        {acik && (
          <div className="mt-4 rounded-lg border border-slate-200 bg-slate-50 p-3">
            <label className="mb-1 block text-xs font-medium text-slate-600">
              Erişim token'ı (kullanıcı ya da Sayfa token'ı)
            </label>
            <textarea
              value={token}
              onChange={(e) => setToken(e.target.value)}
              rows={3}
              placeholder="EAA..."
              className="w-full rounded-lg border border-slate-200 px-3 py-2 font-mono text-xs focus:border-violet-400 focus:outline-none"
            />
            <p className="mt-1.5 text-[11px] text-slate-500">
              Kullanıcı token'ı girerseniz süresiz Sayfa token'ına otomatik çevrilir ve
              şifrelenerek saklanır. Kaydetmeden önce Meta'ya doğrulatılır.
            </p>
            {hata && <div className="mt-2"><Uyari>{hata}</Uyari></div>}
            <div className="mt-2 flex gap-2">
              <button
                onClick={kaydet}
                disabled={!token.trim() || kaydediliyor}
                className="inline-flex items-center gap-1.5 rounded-lg bg-violet-600 px-3 py-1.5 text-xs font-semibold text-white hover:bg-violet-700 disabled:opacity-40"
              >
                {kaydediliyor && <Loader2 className="h-3.5 w-3.5 animate-spin" />}
                Doğrula ve kaydet
              </button>
              <button
                onClick={() => { setAcik(false); setHata(null); }}
                className="rounded-lg border border-slate-200 px-3 py-1.5 text-xs font-medium text-slate-600 hover:bg-white"
              >
                Vazgeç
              </button>
            </div>
          </div>
        )}
      </div>
    </section>
  );
}

/* ─────────────────────────── besteci ─────────────────────────── */

function Besteci({ bagli, onTaslak }) {
  const [sekme, setSekme] = useState('gemini');
  const [konu, setKonu] = useState('');
  const [gorselUrl, setGorselUrl] = useState('');
  const [prompt, setPrompt] = useState(null);
  const [caption, setCaption] = useState('');
  const [uretiliyor, setUretiliyor] = useState(false);
  const [kaydediliyor, setKaydediliyor] = useState(false);
  const [hata, setHata] = useState(null);

  const uret = async () => {
    setUretiliyor(true); setHata(null);
    try {
      const r = await cagir('/image', { method: 'POST', body: JSON.stringify({ konu: konu.trim() }) });
      setGorselUrl(r.url); setPrompt(r.prompt);
    } catch (e) { setHata(e.message); } finally { setUretiliyor(false); }
  };

  const kaydet = async () => {
    setKaydediliyor(true); setHata(null);
    try {
      await onTaslak({ mediaUrl: gorselUrl.trim(), caption, prompt });
      setGorselUrl(''); setCaption(''); setPrompt(null); setKonu('');
    } catch (e) { setHata(e.message); } finally { setKaydediliyor(false); }
  };

  const kalan = CAPTION_SINIRI - caption.length;

  return (
    <section className="rounded-xl border border-slate-200 bg-white shadow-sm">
      <div className="border-b border-slate-100 px-5 py-3">
        <h2 className="flex items-center gap-2 text-sm font-semibold text-slate-800">
          <ImageIcon className="h-4 w-4 text-slate-400" /> Yeni gönderi
        </h2>
      </div>

      <div className="grid gap-5 px-5 py-4 md:grid-cols-[260px_1fr]">
        {/* görsel */}
        <div>
          <div className="mb-2 flex gap-1 rounded-lg bg-slate-100 p-0.5 text-xs">
            {[['gemini', 'Gemini üretsin'], ['url', 'URL yapıştır']].map(([k, e]) => (
              <button
                key={k}
                onClick={() => setSekme(k)}
                className={`flex-1 rounded-md px-2 py-1.5 font-medium transition ${
                  sekme === k ? 'bg-white text-slate-800 shadow-sm' : 'text-slate-500 hover:text-slate-700'}`}
              >
                {e}
              </button>
            ))}
          </div>

          {sekme === 'gemini' ? (
            <>
              <textarea
                value={konu}
                onChange={(e) => setKonu(e.target.value)}
                rows={3}
                placeholder="Ne anlatsın? Örn: veri panosuna bakan bir analist masası"
                className="w-full rounded-lg border border-slate-200 px-3 py-2 text-sm focus:border-violet-400 focus:outline-none"
              />
              <button
                onClick={uret}
                disabled={!konu.trim() || uretiliyor}
                className="mt-2 inline-flex w-full items-center justify-center gap-1.5 rounded-lg bg-slate-900 px-3 py-2 text-xs font-semibold text-white hover:bg-slate-800 disabled:opacity-40"
              >
                {uretiliyor ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Sparkles className="h-3.5 w-3.5" />}
                {uretiliyor ? 'Üretiliyor (30-60 sn)…' : 'Görseli üret'}
              </button>
              <p className="mt-1.5 text-[11px] text-slate-400">
                4:5, üst %45 boş bırakılır, görselde yazı olmaz.
              </p>
            </>
          ) : (
            <>
              <input
                value={gorselUrl}
                onChange={(e) => setGorselUrl(e.target.value)}
                placeholder="https://…/gorsel.jpg"
                className="w-full rounded-lg border border-slate-200 px-3 py-2 text-sm focus:border-violet-400 focus:outline-none"
              />
              <p className="mt-1.5 text-[11px] text-slate-400">
                Herkese açık, JPEG, en fazla 8 MB, oran 4:5 – 1.91:1.
              </p>
            </>
          )}

          {gorselUrl && (
            <div className="mt-3 overflow-hidden rounded-lg border border-slate-200 bg-slate-50">
              <img src={gorselUrl} alt="" className="w-full object-contain" />
            </div>
          )}
        </div>

        {/* açıklama */}
        <div className="flex flex-col">
          <textarea
            value={caption}
            onChange={(e) => setCaption(e.target.value.slice(0, CAPTION_SINIRI))}
            rows={10}
            placeholder="Açıklama…"
            className="w-full flex-1 resize-none rounded-lg border border-slate-200 px-3 py-2 text-sm leading-relaxed focus:border-violet-400 focus:outline-none"
          />
          <div className="mt-1.5 flex items-center justify-between text-[11px]">
            <span className="text-slate-400">
              Yapay zekâ ile üretilen görsellerde açıklamaya bunu belirten bir not eklemek
              1 Ağustos 2026'da yürürlüğe giren Ticari Reklam Yönetmeliği açısından gerekli olabilir.
            </span>
            <span className={`tabular-nums ${kalan < 100 ? 'text-amber-600' : 'text-slate-400'}`}>{kalan}</span>
          </div>

          {hata && <div className="mt-3"><Uyari>{hata}</Uyari></div>}

          <div className="mt-3 flex items-center gap-2">
            <button
              onClick={kaydet}
              disabled={!bagli || !gorselUrl.trim() || kaydediliyor}
              className="inline-flex items-center gap-1.5 rounded-lg bg-violet-600 px-4 py-2 text-xs font-semibold text-white hover:bg-violet-700 disabled:opacity-40"
            >
              {kaydediliyor && <Loader2 className="h-3.5 w-3.5 animate-spin" />}
              Taslak olarak kaydet
            </button>
            <span className="text-[11px] text-slate-400">
              Yayın ayrı bir adım — taslaklar aşağıda.
            </span>
          </div>
        </div>
      </div>
    </section>
  );
}

/* ─────────────────────────── carousel bestecisi ───────────────────────────
 * Konu → kurgu → GERÇEK panel kartları → 5 slaytlık carousel.
 *
 * Görsel dil bilerek Gemini sekmesinden farklı: açık milimetrik zemin, siyah
 * tipografi, uçuşan panel ekranları, elle çizilmiş oklar. Kanıt değeri kartların
 * uydurma değil panelin kendisi olmasından geliyor.
 *
 * 🔴 Kartlar müşteri markalarının canlı verisi. Marka adı ve logosu ekran
 * alınmadan ÖNCE backend'de siliniyor; "görünen ad" o markanın yerine geçer.
 */
function CarouselBestecisi({ bagli, onUretildi }) {
  const [secenekler, setSecenekler] = useState(null);
  const [konu, setKonu] = useState('');
  const [kaynak, setKaynak] = useState('panel');
  const [brandId, setBrandId] = useState('');
  const [takmaAd, setTakmaAd] = useState('Marka');
  const [uretiliyor, setUretiliyor] = useState(false);
  const [sonuc, setSonuc] = useState(null);
  const [hata, setHata] = useState(null);

  useEffect(() => {
    cagir('/carousel/options')
      .then((o) => {
        setSecenekler(o);
        if (o.markalar?.length) setBrandId(o.markalar[0].id);
      })
      .catch((e) => setHata(e.message));
  }, []);

  const uret = async () => {
    setUretiliyor(true); setHata(null); setSonuc(null);
    try {
      const r = await cagir('/carousel', {
        method: 'POST',
        body: JSON.stringify({
          konu: konu.trim(), kaynak,
          ...(kaynak === 'panel' ? { brandId, takmaAd: takmaAd.trim() || 'Marka' } : {}),
        }),
      });
      setSonuc(r);
      await onUretildi();
    } catch (e) { setHata(e.message); } finally { setUretiliyor(false); }
  };

  return (
    <section className="rounded-xl border border-slate-200 bg-white shadow-sm">
      <div className="flex flex-wrap items-center gap-2 border-b border-slate-100 px-5 py-3">
        <h2 className="flex items-center gap-2 text-sm font-semibold text-slate-800">
          <Layers className="h-4 w-4 text-slate-400" /> Carousel üret
        </h2>
        <Rozet tur="mor">gerçek panel ekranları</Rozet>
      </div>

      <div className="grid gap-5 px-5 py-4 md:grid-cols-[1fr_280px]">
        <div>
          <label className="mb-1 block text-[11px] font-medium text-slate-500">Konu</label>
          <textarea
            value={konu}
            onChange={(e) => setKonu(e.target.value)}
            rows={4}
            placeholder="Ne anlatalım? Örn: marka sahibi her sabah üç ayrı panel açıp Excel'de birleştiriyor; Glowytics bunu tek ekranda topluyor."
            className="w-full rounded-lg border border-slate-200 px-3 py-2 text-sm focus:border-violet-400 focus:outline-none"
          />
          <p className="mt-1.5 text-[11px] text-slate-400">
            5 slayt: kanca → mekanizma → dönüş → kanıt → kapanış. Metni ve açıklamayı
            model yazar, yerleşimi şablonlar kurar.
          </p>
        </div>

        <div className="space-y-3">
          <div>
            <label className="mb-1 block text-[11px] font-medium text-slate-500">Kart görselleri</label>
            <div className="flex gap-1 rounded-lg bg-slate-100 p-0.5 text-xs">
              {[['panel', 'Panel ekranı'], ['gemini', 'Gemini çizsin']].map(([k, e]) => (
                <button
                  key={k}
                  onClick={() => setKaynak(k)}
                  className={`flex-1 rounded-md px-2 py-1.5 font-medium transition ${
                    kaynak === k ? 'bg-white text-slate-800 shadow-sm' : 'text-slate-500 hover:text-slate-700'}`}
                >
                  {e}
                </button>
              ))}
            </div>
          </div>

          {kaynak === 'panel' ? (
            <>
              <div>
                <label className="mb-1 block text-[11px] font-medium text-slate-500">
                  Kartlar hangi markanın panelinden?
                </label>
                <select
                  value={brandId}
                  onChange={(e) => setBrandId(e.target.value)}
                  className="w-full rounded-lg border border-slate-200 px-3 py-2 text-sm focus:border-violet-400 focus:outline-none"
                >
                  {(secenekler?.markalar || []).map((m) => (
                    <option key={m.id} value={m.id}>{m.name}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="mb-1 block text-[11px] font-medium text-slate-500">
                  Görselde görünecek ad
                </label>
                <input
                  value={takmaAd}
                  onChange={(e) => setTakmaAd(e.target.value)}
                  className="w-full rounded-lg border border-slate-200 px-3 py-2 text-sm focus:border-violet-400 focus:outline-none"
                />
                <p className="mt-1 text-[11px] text-slate-400">
                  Marka adı ve logosu ekranlardan silinir, yerine bu yazılır.
                  <b className="font-semibold text-slate-500"> Rakamlar da uydurmayla değiştirilir.</b>
                </p>
              </div>
            </>
          ) : (
            <p className="rounded-lg bg-slate-50 px-3 py-2 text-[11px] leading-snug text-slate-500">
              Panel hiç açılmaz, müşteri verisi kullanılmaz. Kartların yerine Gemini
              soyut nesne çizer: mavi 3B kil, açık zemin, yazısız. Marka seçimi gerekmez.
            </p>
          )}

          <button
            onClick={uret}
            disabled={!bagli || !konu.trim() || uretiliyor || (kaynak === 'panel' && !brandId)}
            className="inline-flex w-full items-center justify-center gap-1.5 rounded-lg bg-slate-900 px-3 py-2 text-xs font-semibold text-white hover:bg-slate-800 disabled:opacity-40"
          >
            {uretiliyor ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Sparkles className="h-3.5 w-3.5" />}
            {uretiliyor ? 'Üretiliyor (2-3 dk)…' : 'Carousel üret'}
          </button>
          {uretiliyor && (
            <p className="text-[11px] leading-snug text-slate-400">
              {kaynak === 'panel'
                ? 'Her panel kartı için ayrı sayfa açılıp verinin yüklenmesi bekleniyor. Sekmeyi kapatma.'
                : 'Her görsel için Gemini çağrılıyor. Sekmeyi kapatma.'}
            </p>
          )}
        </div>
      </div>

      {hata && <div className="px-5 pb-4"><Uyari>{hata}</Uyari></div>}

      {sonuc && (
        <div className="border-t border-slate-100 px-5 py-4">
          <div className="mb-2 flex flex-wrap items-center gap-2">
            <Rozet tur="iyi"><CheckCircle2 className="h-3 w-3" /> {sonuc.urller.length} slayt taslağa eklendi</Rozet>
            {sonuc.uyarilar?.map((u) => <Rozet key={u} tur="uyari"><AlertTriangle className="h-3 w-3" /> {u}</Rozet>)}
          </div>
          <div className="flex gap-2 overflow-x-auto pb-1">
            {sonuc.urller.map((u, i) => (
              <img
                key={u} src={u} alt={`slayt ${i + 1}`}
                className="h-36 w-36 flex-shrink-0 rounded-lg border border-slate-200 object-cover"
              />
            ))}
          </div>
        </div>
      )}
    </section>
  );
}

/* ─────────────────────────── taslak detayı ───────────────────────────
 * Listede slaytlar küçücük görünüyordu ve carousel'in ne anlattığı okunmuyordu.
 * Yayın geri alınamadığı için gönderinin TAMAMI yayından önce görülebilmeli:
 * bu pencere slaytları tam boy gezdirir, açıklamayı düzenletir, kurguyu açar.
 */
function TaslakDetay({ post, onKapat, onYayinla, onSil, onGuncelle }) {
  const slaytlar = useMemo(
    () => (post.mediaUrls?.length ? post.mediaUrls : [post.mediaUrl]).filter(Boolean),
    [post],
  );
  const [i, setI] = useState(0);
  const [caption, setCaption] = useState(post.caption || '');
  const [kaydediliyor, setKaydediliyor] = useState(false);
  const [calisiyor, setCalisiyor] = useState(false);
  const [onay, setOnay] = useState(false);
  const [hata, setHata] = useState(null);

  const yayinlandi = post.status === 'PUBLISHED';
  const kurgu = post.planJson || null;
  const degisti = (post.caption || '') !== caption;

  const git = useCallback(
    (yon) => setI((x) => (x + yon + slaytlar.length) % slaytlar.length),
    [slaytlar.length],
  );

  // Escape kapatır, oklar slayt gezer — pencere açıkken sayfa kaymasın diye
  // gövde kaydırması da kilitleniyor.
  useEffect(() => {
    const tus = (e) => {
      if (e.key === 'Escape') onKapat();
      else if (e.key === 'ArrowRight') git(1);
      else if (e.key === 'ArrowLeft') git(-1);
    };
    window.addEventListener('keydown', tus);
    const eski = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      window.removeEventListener('keydown', tus);
      document.body.style.overflow = eski;
    };
  }, [onKapat, git]);

  const captionKaydet = async () => {
    setKaydediliyor(true); setHata(null);
    try {
      await onGuncelle(post.id, { caption });
    } catch (e) { setHata(e.message); } finally { setKaydediliyor(false); }
  };

  const yayinla = async () => {
    setCalisiyor(true); setHata(null);
    try {
      await onYayinla(post.id);
      onKapat();
    } catch (e) { setHata(e.message); setOnay(false); } finally { setCalisiyor(false); }
  };

  const sil = async () => {
    if (!window.confirm('Bu taslak silinecek. Devam?')) return;
    try { await onSil(post.id); onKapat(); } catch (e) { setHata(e.message); }
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/60 p-4 backdrop-blur-sm"
      onMouseDown={(e) => { if (e.target === e.currentTarget) onKapat(); }}
    >
      <div className="flex max-h-[92vh] w-full max-w-5xl flex-col overflow-hidden rounded-2xl bg-white shadow-2xl">
        {/* başlık */}
        <div className="flex flex-shrink-0 items-center gap-2 border-b border-slate-100 px-5 py-3">
          <h3 className="text-sm font-semibold text-slate-800">
            {yayinlandi ? 'Yayınlanan gönderi' : 'Taslak'}
          </h3>
          {slaytlar.length > 1 && (
            <Rozet tur="mor"><Layers className="h-3 w-3" /> {slaytlar.length} slayt</Rozet>
          )}
          <span className="text-[11px] text-slate-400">{tarih(post.createdAt)}</span>
          <button
            onClick={onKapat}
            className="ml-auto rounded-lg p-1.5 text-slate-400 hover:bg-slate-100 hover:text-slate-600"
            aria-label="Kapat"
          >
            <X className="h-4 w-4" />
          </button>
        </div>

        <div className="grid min-h-0 flex-1 gap-0 overflow-hidden md:grid-cols-[minmax(0,1fr)_340px]">
          {/* slayt gezgini */}
          <div className="flex min-h-0 flex-col bg-slate-50">
            <div className="relative flex min-h-0 flex-1 items-center justify-center p-4">
              <img
                src={slaytlar[i]}
                alt={`slayt ${i + 1}`}
                className="max-h-[58vh] w-auto max-w-full rounded-xl border border-slate-200 bg-white object-contain shadow-sm"
              />
              {slaytlar.length > 1 && (
                <>
                  <button
                    onClick={() => git(-1)}
                    className="absolute left-6 rounded-full bg-white/90 p-2 text-slate-700 shadow ring-1 ring-slate-200 hover:bg-white"
                    aria-label="Önceki slayt"
                  >
                    <ChevronLeft className="h-4 w-4" />
                  </button>
                  <button
                    onClick={() => git(1)}
                    className="absolute right-6 rounded-full bg-white/90 p-2 text-slate-700 shadow ring-1 ring-slate-200 hover:bg-white"
                    aria-label="Sonraki slayt"
                  >
                    <ChevronRight className="h-4 w-4" />
                  </button>
                </>
              )}
            </div>

            {slaytlar.length > 1 && (
              <div className="flex flex-shrink-0 items-center gap-2 border-t border-slate-200 bg-white px-4 py-2">
                <span className="text-[11px] tabular-nums text-slate-400">{i + 1}/{slaytlar.length}</span>
                <div className="flex gap-1.5 overflow-x-auto">
                  {slaytlar.map((u, k) => (
                    <button
                      key={u}
                      onClick={() => setI(k)}
                      className={`h-14 w-14 flex-shrink-0 overflow-hidden rounded-md border-2 transition ${
                        k === i ? 'border-violet-500' : 'border-transparent opacity-60 hover:opacity-100'}`}
                    >
                      <img src={u} alt="" className="h-full w-full object-cover" />
                    </button>
                  ))}
                </div>
                <a
                  href={slaytlar[i]} target="_blank" rel="noreferrer"
                  className="ml-auto inline-flex flex-shrink-0 items-center gap-1 text-[11px] text-slate-400 hover:text-violet-600"
                >
                  tam boy <ExternalLink className="h-3 w-3" />
                </a>
              </div>
            )}
          </div>

          {/* açıklama + kurgu + eylemler */}
          <div className="flex min-h-0 flex-col overflow-y-auto border-l border-slate-100">
            <div className="border-b border-slate-100 p-4">
              <label className="mb-1 block text-[11px] font-medium text-slate-500">Açıklama</label>
              <textarea
                value={caption}
                onChange={(e) => setCaption(e.target.value.slice(0, CAPTION_SINIRI))}
                rows={8}
                disabled={yayinlandi}
                placeholder="Açıklama…"
                className="w-full resize-none rounded-lg border border-slate-200 px-3 py-2 text-sm leading-relaxed focus:border-violet-400 focus:outline-none disabled:bg-slate-50 disabled:text-slate-500"
              />
              <div className="mt-1.5 flex items-center justify-between">
                <span className={`text-[11px] tabular-nums ${
                  CAPTION_SINIRI - caption.length < 100 ? 'text-amber-600' : 'text-slate-400'}`}>
                  {CAPTION_SINIRI - caption.length}
                </span>
                {!yayinlandi && degisti && (
                  <button
                    onClick={captionKaydet}
                    disabled={kaydediliyor}
                    className="inline-flex items-center gap-1 rounded-md bg-slate-900 px-2.5 py-1 text-[11px] font-semibold text-white hover:bg-slate-800 disabled:opacity-40"
                  >
                    {kaydediliyor && <Loader2 className="h-3 w-3 animate-spin" />} açıklamayı kaydet
                  </button>
                )}
              </div>
            </div>

            {kurgu && (
              <div className="border-b border-slate-100 p-4">
                <h4 className="mb-2 text-[11px] font-semibold uppercase tracking-wide text-slate-400">Kurgu</h4>
                {kurgu.konu && (
                  <p className="mb-3 rounded-lg bg-slate-50 px-3 py-2 text-[12px] leading-snug text-slate-600">
                    {kurgu.konu}
                  </p>
                )}
                <ol className="space-y-2">
                  {(kurgu.slaytlar || []).map((s, k) => (
                    <li key={k}>
                      <button
                        onClick={() => setI(k)}
                        className={`w-full rounded-lg px-2 py-1.5 text-left transition ${
                          k === i ? 'bg-violet-50 ring-1 ring-violet-200' : 'hover:bg-slate-50'}`}
                      >
                        <span className="text-[10px] font-semibold uppercase tracking-wide text-slate-400">
                          {k + 1}. {s.duzen}
                        </span>
                        {s.ust && <p className="text-[12px] leading-snug text-slate-700">{s.ust.replace(/\*\*/g, '')}</p>}
                        {s.alt && <p className="text-[12px] leading-snug text-slate-400">{s.alt.replace(/\*\*/g, '')}</p>}
                      </button>
                    </li>
                  ))}
                </ol>
                {kurgu.takmaAd && (
                  <p className="mt-3 text-[11px] text-slate-400">
                    Panel kartlarındaki marka adı <b className="font-semibold text-slate-500">{kurgu.takmaAd}</b> ile
                    değiştirildi. Sayılar gerçek.
                  </p>
                )}
              </div>
            )}

            {post.error && <div className="p-4"><Uyari>{post.error}</Uyari></div>}
            {hata && <div className="p-4 pt-0"><Uyari>{hata}</Uyari></div>}

            {/* Yayın düğmesi kurgu listesinin altında kalıp kaydırma gerektiriyordu;
                yapılacak tek iş buysa görünürde durmalı → sütunun dibine yapışık. */}
            <div className="sticky bottom-0 mt-auto border-t border-slate-100 bg-white p-4">
              {post.permalink && (
                <a
                  href={post.permalink} target="_blank" rel="noreferrer"
                  className="mb-3 inline-flex items-center gap-1 text-xs font-medium text-violet-600 hover:text-violet-700"
                >
                  Instagram'da aç <ExternalLink className="h-3 w-3" />
                </a>
              )}

              {!yayinlandi && (onay ? (
                /* Yayın geri alınamıyor — onay bilinçli olarak iki adımlı. */
                <div className="rounded-lg border border-amber-200 bg-amber-50 p-3">
                  <p className="mb-2 text-[11px] leading-snug text-amber-800">
                    {slaytlar.length} slayt <b>@glowytics</b> hesabında yayınlanacak. Yayınlanan
                    gönderi API'den silinemiyor, yalnızca Instagram uygulamasından elle silinebilir.
                    Emin misiniz?
                  </p>
                  <div className="flex justify-end gap-1.5">
                    <button
                      onClick={() => setOnay(false)}
                      className="rounded-md px-2 py-1 text-[11px] text-slate-600 hover:bg-white"
                    >
                      vazgeç
                    </button>
                    <button
                      onClick={yayinla}
                      disabled={calisiyor}
                      className="inline-flex items-center gap-1 rounded-md bg-rose-600 px-2.5 py-1 text-[11px] font-semibold text-white hover:bg-rose-700 disabled:opacity-50"
                    >
                      {calisiyor && <Loader2 className="h-3 w-3 animate-spin" />} evet, yayınla
                    </button>
                  </div>
                </div>
              ) : (
                <div className="flex items-center gap-2">
                  <button
                    onClick={() => setOnay(true)}
                    disabled={degisti}
                    title={degisti ? 'Önce açıklama değişikliğini kaydedin' : undefined}
                    className="inline-flex flex-1 items-center justify-center gap-1.5 rounded-lg bg-slate-900 px-3 py-2 text-xs font-semibold text-white hover:bg-slate-800 disabled:opacity-40"
                  >
                    <Send className="h-3.5 w-3.5" /> Yayınla
                  </button>
                  <button
                    onClick={sil}
                    className="rounded-lg p-2 text-slate-400 hover:bg-rose-50 hover:text-rose-600"
                    aria-label="Taslağı sil"
                  >
                    <Trash2 className="h-4 w-4" />
                  </button>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

/* ─────────────────────────── taslak satırı ─────────────────────────── */

function TaslakSatiri({ post, onAc }) {
  const slaytlar = (post.mediaUrls?.length ? post.mediaUrls : [post.mediaUrl]).filter(Boolean);

  const rozet = {
    DRAFT: <Rozet tur="nötr"><Clock className="h-3 w-3" /> taslak</Rozet>,
    PUBLISHING: <Rozet tur="uyari"><Loader2 className="h-3 w-3 animate-spin" /> yayınlanıyor</Rozet>,
    PUBLISHED: <Rozet tur="iyi"><CheckCircle2 className="h-3 w-3" /> yayında</Rozet>,
    FAILED: <Rozet tur="kotu"><AlertTriangle className="h-3 w-3" /> başarısız</Rozet>,
  }[post.status];

  // Satırın tamamı tıklanabilir: yayın düğmesi burada DEĞİL, detay penceresinde.
  // Geri alınamayan bir eylem, gönderiyi tam boy görmeden bir tık ötede durmasın.
  return (
    <button
      type="button"
      onClick={() => onAc(post)}
      className="flex w-full gap-4 border-b border-slate-100 px-5 py-4 text-left transition last:border-0 hover:bg-slate-50"
    >
      {slaytlar.length > 1 ? (
        <div className="flex flex-shrink-0 gap-1">
          {slaytlar.slice(0, 3).map((u, i) => (
            <img
              key={u} src={u} alt={`slayt ${i + 1}`}
              className="h-20 w-20 flex-shrink-0 rounded-lg border border-slate-200 object-cover"
            />
          ))}
          {slaytlar.length > 3 && (
            <div className="flex h-20 w-10 flex-shrink-0 items-center justify-center rounded-lg border border-slate-200 bg-slate-50 text-[11px] font-semibold text-slate-400">
              +{slaytlar.length - 3}
            </div>
          )}
        </div>
      ) : (
        <img src={post.mediaUrl} alt="" className="h-20 w-16 flex-shrink-0 rounded-lg border border-slate-200 object-cover" />
      )}

      <div className="min-w-0 flex-1">
        <div className="flex flex-wrap items-center gap-2">
          {rozet}
          {slaytlar.length > 1 && (
            <Rozet tur="mor"><Layers className="h-3 w-3" /> {slaytlar.length} slayt</Rozet>
          )}
          <span className="text-[11px] text-slate-400">{tarih(post.createdAt)}</span>
        </div>
        <p className="mt-1.5 whitespace-pre-wrap break-words text-sm text-slate-700 line-clamp-2">
          {post.caption || <span className="text-slate-400">(açıklama yok)</span>}
        </p>
        {post.error && (
          <p className="mt-1 truncate text-[11px] text-rose-600">{post.error}</p>
        )}
      </div>

      <div className="flex flex-shrink-0 items-center self-center text-slate-300">
        <ChevronRight className="h-5 w-5" />
      </div>
    </button>
  );
}

/* ─────────────────────────── sayfa ─────────────────────────── */

export default function InstagramStudio() {
  const [durum, setDurum] = useState(null);
  const [postlar, setPostlar] = useState([]);
  const [yukleniyor, setYukleniyor] = useState(true);
  const [hata, setHata] = useState(null);

  const yukle = useCallback(async () => {
    setYukleniyor(true); setHata(null);
    try {
      const d = await cagir('/connection');
      setDurum(d);
      setPostlar(d.bagli ? await cagir('/posts') : []);
    } catch (e) { setHata(e.message); } finally { setYukleniyor(false); }
  }, []);

  useEffect(() => { yukle(); }, [yukle]);

  const kaydetToken = async (accessToken) => {
    setDurum(await cagir('/connection', { method: 'POST', body: JSON.stringify({ accessToken }) }));
    setPostlar(await cagir('/posts'));
  };

  const silBaglanti = async () => {
    if (!window.confirm('Bağlantı ve gönderi geçmişi silinecek. Instagram\'daki gönderiler etkilenmez. Devam?')) return;
    await cagir('/connection', { method: 'DELETE' });
    await yukle();
  };

  const taslakEkle = async (govde) => {
    const yeni = await cagir('/posts', { method: 'POST', body: JSON.stringify(govde) });
    setPostlar((p) => [yeni, ...p]);
  };

  const yayinla = async (id) => {
    try {
      const r = await cagir(`/posts/${id}/publish`, { method: 'POST' });
      setPostlar((p) => p.map((x) => (x.id === id ? r.post : x)));
      setDurum(await cagir('/connection')); // kota güncellensin
    } catch (e) {
      setPostlar(await cagir('/posts')); // hata mesajı kayda yazıldı, oradan okuyalım
      setHata(e.message);
      throw e; // detay penceresi açık kalsın ve hatayı göstersin
    }
  };

  const silPost = async (id) => {
    await cagir(`/posts/${id}`, { method: 'DELETE' });
    setPostlar((p) => p.filter((x) => x.id !== id));
  };

  const guncellePost = async (id, govde) => {
    const yeni = await cagir(`/posts/${id}`, { method: 'PATCH', body: JSON.stringify(govde) });
    setPostlar((p) => p.map((x) => (x.id === id ? yeni : x)));
  };

  const [taslaklar, gecmis] = useMemo(() => [
    postlar.filter((p) => p.status !== 'PUBLISHED'),
    postlar.filter((p) => p.status === 'PUBLISHED'),
  ], [postlar]);

  // Detay penceresi id ile tutulur, nesneyle değil: yayınla/açıklama kaydet
  // sonrası liste tazelenince pencere de güncel kaydı göstersin.
  const [acikId, setAcikId] = useState(null);
  const acik = useMemo(() => postlar.find((p) => p.id === acikId) || null, [postlar, acikId]);

  return (
    <div className="min-h-screen bg-slate-50 px-6 py-6">
      <div className="mx-auto max-w-5xl space-y-5">
        <header className="flex flex-wrap items-center gap-3">
          <div className="rounded-xl bg-gradient-to-br from-violet-500 to-fuchsia-500 p-2 text-white">
            <Instagram className="h-5 w-5" />
          </div>
          <div>
            <h1 className="flex items-center gap-2 text-xl font-bold text-slate-900">
              Instagram Stüdyosu <Rozet tur="mor">beta</Rozet>
            </h1>
            <p className="text-xs text-slate-500">
              Glowytics'in kendi hesabı için içerik üret, gözden geçir, yayınla. Marka bağımsız iç araç.
            </p>
          </div>
        </header>

        {hata && <Uyari>{hata}</Uyari>}

        <BaglantiKarti
          durum={durum}
          yukleniyor={yukleniyor}
          onYenile={yukle}
          onKaydet={kaydetToken}
          onSil={silBaglanti}
        />

        <CarouselBestecisi
          bagli={!!durum?.bagli && durum?.gecerli !== false}
          onUretildi={async () => setPostlar(await cagir('/posts'))}
        />

        <Besteci bagli={!!durum?.bagli && durum?.gecerli !== false} onTaslak={taslakEkle} />

        <section className="rounded-xl border border-slate-200 bg-white shadow-sm">
          <div className="border-b border-slate-100 px-5 py-3">
            <h2 className="text-sm font-semibold text-slate-800">
              Taslaklar {taslaklar.length > 0 && <span className="text-slate-400">({taslaklar.length})</span>}
            </h2>
          </div>
          {taslaklar.length === 0 ? (
            <p className="px-5 py-6 text-sm text-slate-400">Bekleyen taslak yok.</p>
          ) : (
            taslaklar.map((p) => (
              <TaslakSatiri key={p.id} post={p} onAc={(x) => setAcikId(x.id)} />
            ))
          )}
        </section>

        {gecmis.length > 0 && (
          <section className="rounded-xl border border-slate-200 bg-white shadow-sm">
            <div className="border-b border-slate-100 px-5 py-3">
              <h2 className="text-sm font-semibold text-slate-800">
                Yayınlananlar <span className="text-slate-400">({gecmis.length})</span>
              </h2>
            </div>
            {gecmis.map((p) => (
              <TaslakSatiri key={p.id} post={p} onAc={(x) => setAcikId(x.id)} />
            ))}
          </section>
        )}
      </div>

      {acik && (
        <TaslakDetay
          post={acik}
          onKapat={() => setAcikId(null)}
          onYayinla={yayinla}
          onSil={silPost}
          onGuncelle={guncellePost}
        />
      )}
    </div>
  );
}
